// SalaryDetailsModal.tsx

import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Table, DropdownButton, Dropdown } from 'react-bootstrap';
import { getUserSalariesByMonthYear } from '../../../../../../../../crud/management_api/user_salaries';
import { UserSalary } from '../../../../types';
import { formatCurrency } from '../../../../../../../../utils/functions';
import { X } from 'react-bootstrap-icons';
import './SalaryDetailsModal.scss';
import axios from 'axios';

interface SalaryDetailsModalProps {
  show: boolean;
  handleClose: () => void;
  userId: number;
  username: string;
}

const months = [
  { label: 'Enero', value: '01' },
  { label: 'Febrero', value: '02' },
  { label: 'Marzo', value: '03' },
  { label: 'Abril', value: '04' },
  { label: 'Mayo', value: '05' },
  { label: 'Junio', value: '06' },
  { label: 'Julio', value: '07' },
  { label: 'Agosto', value: '08' },
  { label: 'Septiembre', value: '09' },
  { label: 'Octubre', value: '10' },
  { label: 'Noviembre', value: '11' },
  { label: 'Diciembre', value: '12' },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => String(currentYear - i));

const SalaryDetailsModal: React.FC<SalaryDetailsModalProps> = ({
  show,
  handleClose,
  userId,
  username,
}) => {
  const [salaries, setSalaries] = useState<UserSalary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string>(
    (`0${new Date().getMonth() + 1}`).slice(-2)
  );
  const [selectedYear, setSelectedYear] = useState<string>(
    String(new Date().getFullYear())
  );

  useEffect(() => {
    if (!show) return;
    (async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const userStored = JSON.parse(localStorage.getItem('user') || '{}') as {
          local_id?: number;
        };
        const localId = userStored.local_id ?? 0;
        const monthYear = `${selectedMonth}${selectedYear.slice(-2)}`;
        const response = await (getUserSalariesByMonthYear as any)(
          localId,
          monthYear,
          userId
        );
        const data = Array.isArray(response)
          ? response
          : (response.data as UserSalary[]);
        setSalaries(data);
      } catch (err: unknown) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setNotFound(true);
          setSalaries([]);
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [show, selectedMonth, selectedYear, userId]);

  const monthLabel = months.find((m) => m.value === selectedMonth)?.label ?? '';

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            Pagos de {username}
          </Modal.Title>
        </div>
        <Button variant="none" onClick={handleClose}>
          <X size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body>
        <Form className="dropdowns-container">
          <Form.Group className="mp-formgroup horizontal">
            <Form.Label>Mes</Form.Label>
            <DropdownButton
              className="mp-dropdown"
              variant="none"
              id="month-dropdown"
              title={monthLabel}
              onSelect={(value) => value && setSelectedMonth(value)}
            >
              {months.map((m) => (
                <Dropdown.Item
                  key={m.value}
                  eventKey={m.value}
                >
                  {m.label}
                </Dropdown.Item>
              ))}
            </DropdownButton>
          </Form.Group>

          <Form.Group className="mp-formgroup horizontal">
            <Form.Label>Año</Form.Label>
            <DropdownButton
              className="mp-dropdown"
              variant="none"
              id="year-dropdown"
              title={selectedYear}
              onSelect={(value) => value && setSelectedYear(value)}
            >
              {years.map((y) => (
                <Dropdown.Item
                  key={y}
                  eventKey={y}
                >
                  {y}
                </Dropdown.Item>
              ))}
            </DropdownButton>
          </Form.Group>
        </Form>

        {error && <div className="text-danger">{error}</div>}

        {loading ? (
          <div>Cargando pagos…</div>
        ) : notFound ? (
          <div className="text-muted">No existen pagos con esa fecha.</div>
        ) : (
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            <Table className="custom-table">
              <thead>
                <tr>
                  <th>#Pago</th>
                  <th>Mes pagado</th>
                  <th>Fecha del pago</th>
                  <th>Total Pagado</th>
                  <th>Neto Pagado</th>
                </tr>
              </thead>
              <tbody>
                {salaries.map((item) => {
                  const ts = item.timestamp
                    ? new Date(item.timestamp).toLocaleString()
                    : '-';
                  return (
                    <tr key={item.salary_id}>
                      <td>{item.user_payment_number}</td>
                      <td>{item.month_year}</td>
                      <td>{ts}</td>
                      <td>{formatCurrency(item.total_payed)}</td>
                      <td>
                        {item.net_payed != null
                          ? formatCurrency(item.net_payed)
                          : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SalaryDetailsModal;
